import {
  all,
  call,
  cancel,
  delay,
  fork,
  put,
  retry,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { channel } from 'redux-saga';
import { liveActions, liveSelector } from '$store/live/index';
import { toastActions } from '$store/toast';
import { Api } from '$api';
import {
  GetOnlineUsersAction,
  GetTotalTipsAction,
  LiveState,
  LiveStatus,
  LiveType,
  startWatchLiveAction,
} from '$store/live/interface';
import { mainActions } from '$store/main';
import { authSelector } from '$store/auth';
import { AuthState } from '$store/auth/interface';
import { hideModal } from '$navigation/helper';
import { batchActions } from '$store';
import { profileActions } from '$store/profile';
import { t } from '$translation/helper';
import { blacklistActions } from '$store/blacklist';
import { AttachType, MessageModel, StreamMessageModelType } from '$store/models';
import { statReachGoal } from '$utils';
import { getMainCamerasFromDevices } from '$utils/camera';

let tipsSound: HTMLAudioElement;
function* initTipsSoundWorker() {
  if (tipsSound) return;
  yield call(() => (tipsSound = new Audio('/sounds/tips.mp3')));
}

function* playTipsSoundWorker() {
  if (!tipsSound) {
    yield initTipsSoundWorker();
  }
  tipsSound.currentTime = 0;
  try {
    yield tipsSound.play();
  } catch (e) {
    console.log('playTipsSound error:', e);
  }
}

let pc = new RTCPeerConnection();

const iceConnectionStateChange = channel<string>();

function* watchIceConnectionStateChannel() {
  while (true) {
    const { type, reconnecting } = (yield select(liveSelector)) as LiveState;
    const state = yield take(iceConnectionStateChange);

    // "checking" | "closed" | "completed" | "connected" | "disconnected" | "failed" | "new";
    console.log('watchIceConnectionStateChannel state:', state);
    switch (state) {
      case 'new':
      case 'checking':
        if (!reconnecting) {
          yield put(liveActions.setLiveStatus(LiveStatus.CONNECTING));
        }
        break;
      case 'connected':
        if (type === LiveType.PUBLISHER && !reconnecting) {
          yield delay(3000);
        }
        yield put(liveActions.setReconnecting(false));
        yield put(liveActions.setTogglingCamera(false));
        yield put(liveActions.setLiveStatus(LiveStatus.ONLINE));
        break;
      case 'connecting':
        yield put(liveActions.setLiveStatus(LiveStatus.CONNECTING));
        break;
      case 'disconnected':
      case 'closed':
      case 'completed':
      case 'failed':
        yield put(liveActions.reconnect());
        break;
      default:
        yield put(liveActions.setLiveStatus(LiveStatus.DISCONNECTED));
        break;
    }
  }
}

function* getDevices() {
  return yield call(() => navigator.mediaDevices.enumerateDevices());
}

function* gotDevices(devices: MediaDeviceInfo[]) {
  const cameras = getMainCamerasFromDevices(devices);
  yield put(liveActions.setCameras(cameras));
}

function* getStream() {
  const { currentCameraId } = (yield select(liveSelector)) as LiveState;
  yield stopStreamWorker();

  return yield call(() =>
    navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: currentCameraId || undefined,
        facingMode: currentCameraId ? undefined : 'user',
      },
      audio: true,
    }),
  );
}

function* gotStream(stream: MediaStream) {
  yield put(liveActions.setMediaStream(stream));
}

function* createLiveWorker() {
  const { mediaStream, permission } = (yield select(liveSelector)) as LiveState;
  yield put(liveActions.setLiveStatus(LiveStatus.CREATING));
  pc = new RTCPeerConnection();
  pc.oniceconnectionstatechange = () => {
    iceConnectionStateChange.put(pc.iceConnectionState);
  };

  mediaStream &&
    mediaStream.getTracks().forEach((track) => {
      pc.addTrack(track);
    });

  const offer = yield call(() => pc.createOffer());
  yield call(() => pc.setLocalDescription(offer));

  let data: any;

  try {
    data = yield call(Api.post, '/streaming/create', {
      type: permission,
      sdp: offer.sdp,
    });
    yield put(liveActions.setLiveStatus(LiveStatus.CONNECTING));
    yield put(liveActions.setStream(data.stream));
  } catch (e) {
    console.error(e.message);
    yield put(liveActions.setReconnecting(true));
    yield put(liveActions.setTogglingCamera(false));
    yield delay(1000);
    yield put(liveActions.reconnect());
    return;
  }

  try {
    yield call(() =>
      pc.setRemoteDescription(
        new RTCSessionDescription({ type: 'answer', sdp: data.stream.sdp }),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(t('live_toast_connection_error')));
    return yield stopStreamWorker();
  }

  yield fork(startHistoryLoopWorker);
}

function* startStreamWorker() {
  const { cameras, micEnabled } = (yield select(liveSelector)) as LiveState;
  let stream: MediaStream;

  try {
    stream = yield getStream();
    const { type } = (yield select(liveSelector)) as LiveState;
    if (type === LiveType.VIEWER) {
      // Если доступ к камере получили позже чем закрыли модалку стрима
      // завершаем стрим
      yield stopStreamWorker();
      return false;
    }
  } catch (error) {
    try {
      const [mic, cam] = (yield all(
        // @ts-ignore
        ['microphone', 'camera'].map((name) => navigator.permissions.query({ name })),
      )).map((p) => p.state === 'granted');
      if (!cam && !mic) {
        yield put(liveActions.setLiveStatus(LiveStatus.ACCESS_CAM_AND_MIC_ERROR));
        return false;
      } else if (!cam) {
        yield put(liveActions.setLiveStatus(LiveStatus.ACCESS_CAM_ERROR));
        return false;
      } else if (!mic) {
        yield put(liveActions.setLiveStatus(LiveStatus.ACCESS_MIC_ERROR));
        return false;
      }
    } catch (e) {}

    yield put(liveActions.setLiveStatus(LiveStatus.ACCESS_CAM_ERROR));
    return false;
  }

  yield gotStream(stream);
  yield toggleMicWorker();

  if (cameras.length === 0) {
    const devices: MediaDeviceInfo[] = yield getDevices();
    const devicesRow = devices
      .filter((d) => d.kind == 'videoinput')
      .map((d) => d.label)
      .join('; ');

    statReachGoal(`live_get_video_devices`, {
      devices: devicesRow,
    });
    yield gotDevices(devices);
  }

  return true;
}

function* setCameraIdWorker() {
  const { liveStatus } = (yield select(liveSelector)) as LiveState;
  if (yield startStreamWorker()) {
    if (liveStatus === LiveStatus.ONLINE) {
      yield put(liveActions.setReconnecting(true));
      yield createLiveWorker();
    } else {
      yield put(liveActions.setLiveStatus(LiveStatus.READY));
    }
  }
}

function* stopStreamWorker() {
  const { mediaStream } = (yield select(liveSelector)) as LiveState;
  mediaStream &&
    mediaStream.getTracks().forEach((track) => {
      track.stop();
    });
  yield call(() => pc.close());
}

function* stopLiveWorker() {
  try {
    const data = yield call(Api.post, '/streaming/end');
    yield stopStreamWorker();
    yield put(
      batchActions(
        liveActions.setType(LiveType.PUBLISHER),
        liveActions.setLiveStatus(LiveStatus.FINISHED),
        liveActions.setConfirmStop(false),
      ),
    );
    if (data?.summary) {
      yield put(liveActions.setEndStatistics(data.summary));
    } else {
      yield put(liveActions.setEndStatistics(undefined));
    }
  } catch (e) {
    console.error(e.message);
    yield put(toastActions.setToastFail(t('live_toast_error_finish_live')));
  }
}

function* closeLiveEndStatisticsWorker() {
  yield put(liveActions.clear());
  hideModal();
}

function* startWatchLiveWorker(action?: startWatchLiveAction) {
  const { stream } = (yield select(liveSelector)) as LiveState;
  const streamId = action?.payload || stream?.id;

  pc = new RTCPeerConnection();

  pc.oniceconnectionstatechange = () => {
    iceConnectionStateChange.put(pc.iceConnectionState);
  };

  pc.addTransceiver('audio', { direction: 'recvonly' });
  pc.addTransceiver('video', { direction: 'recvonly' });

  const offer = yield call(() => pc.createOffer());
  yield put(liveActions.setType(LiveType.VIEWER));
  yield call(() => pc.setLocalDescription(offer));

  const mediaStream = new MediaStream();
  pc.ontrack = (event) => {
    mediaStream.addTrack(event.track);
  };

  yield put(liveActions.setMediaStream(mediaStream));

  try {
    const data = yield yield retry(
      Infinity,
      1500,
      Api.post,
      '/streaming/play/' + streamId,
      {
        sdp: offer.sdp,
      },
    );

    yield put(
      batchActions(
        liveActions.setStream({
          ...data.stream,
          type: data.stream.streamType,
          streamerId: data.streamer.id, // TODO: Добавить поле streamerId на бэке
        }),
        mainActions.setUser(data.streamer),
      ),
    );
    if (data.stream.streamStatus == 'finished') {
      yield put(liveActions.removeStream(data.stream.id));
      yield put(profileActions.removeStream(data.streamer.id));
      return yield put(liveActions.setLiveStatus(LiveStatus.FINISHED));
    } else if (!data.sdp) {
      return yield put(liveActions.setLiveStatus(LiveStatus.NO_ACCESS));
    }

    try {
      yield call(() =>
        pc.setRemoteDescription(
          new RTCSessionDescription({ type: 'answer', sdp: data.sdp }),
        ),
      );
    } catch (e) {
      yield put(toastActions.setToastFail(t('live_toast_connection_error')));
      return yield stopStreamWorker();
    }

    yield fork(startHistoryLoopWorker);
  } catch (error) {
    if (error.code == 'access_token') {
      return yield put(liveActions.setLiveStatus(LiveStatus.NO_ACCESS));
    }
  }
}

function* getHistoryWorker() {
  const { stream, nextFrom, type, allMessagesLoaded } = (yield select(
    liveSelector,
  )) as LiveState;
  try {
    const [data] = yield all([
      call(Api.post, `/streaming/im/${stream?.id}/history`, {
        startFrom: nextFrom,
      }),
      delay(allMessagesLoaded ? 1500 : 0),
    ]);
    if (Object.keys(data.users).length) {
      yield put(mainActions.setUsers(data.users));
    }
    yield put(liveActions.setHistory(data));

    const messages: MessageModel[] = data.messages;

    if (messages?.length) {
      const { currentUserId } = (yield select(liveSelector)) as AuthState;
      const tips = messages.filter(
        (m) =>
          m.fromId !== currentUserId &&
          m.attachments?.find((attachment) => attachment.type === AttachType.TIPS),
      );
      if (tips.length && allMessagesLoaded) {
        yield fork(playTipsSoundWorker);
      }
    }

    if (data.banned) {
      yield put(blacklistActions.setBanListFromMe(data.banned));
    }

    if (data.stream.streamStatus === 'finished') {
      if (type === LiveType.VIEWER) {
        yield finishLiveWorker();
      } else {
        yield put(liveActions.stopStream());
      }
      yield put(liveActions.stopHistoryLoop());
    }
  } catch (e) {
    if (e.code === 'stream_not_active') {
      yield finishLiveWorker();
      yield put(liveActions.stopHistoryLoop());
    } else {
      console.error(e.message);
    }
  }
}

function* finishLiveWorker() {
  const { stream } = (yield select(liveSelector)) as LiveState;
  yield put(liveActions.setLiveStatus(LiveStatus.FINISHED));
  if (stream) {
    yield put(liveActions.removeStream(stream.id));
    yield put(profileActions.removeStream(stream.streamerId));
  }
}

function* historyLoopWorker() {
  while (true) {
    yield getHistoryWorker();
  }
}

function* checkActiveLive() {
  const { currentUserId } = (yield select(authSelector)) as AuthState;
  try {
    const data = yield call(Api.get, `/streaming/user/${currentUserId}?status=active`);
    return data.streams?.length > 0;
  } catch (e) {
    return false;
  }
}

function* startHistoryLoopWorker() {
  const { isLogged } = yield select(authSelector);
  if (!isLogged) {
    return yield put(liveActions.setLiveStatus(LiveStatus.NO_ACCESS));
  }
  yield initTipsSoundWorker();
  yield put(liveActions.stopHistoryLoop());
  const historyLoop = yield fork(historyLoopWorker);
  yield take([
    liveActions.stopWatchLive,
    liveActions.stopLive,
    liveActions.stopHistoryLoop,
    liveActions.stopStream,
    liveActions.banned,
  ]);
  yield cancel(historyLoop);
}

function* stopWatchLiveWorker() {
  yield stopStreamWorker();
  yield put(liveActions.clear());
}

function* initLiveWorker() {
  yield put(liveActions.setType(LiveType.PUBLISHER));

  const hasActiveLive = yield checkActiveLive();

  if (yield startStreamWorker()) {
    yield put(liveActions.setLiveStatus(LiveStatus.READY));

    if (hasActiveLive) {
      yield createLiveWorker();
    }
  }
}

function* reconnectionWorker() {
  const { type, stream, liveStatus } = (yield select(liveSelector)) as LiveState;
  if (!stream && liveStatus !== LiveStatus.CREATING) return;
  yield put(liveActions.setReconnecting(true));
  yield call(() => pc.close());
  if (type === LiveType.PUBLISHER) {
    yield createLiveWorker();
  } else {
    yield startWatchLiveWorker();
  }
}

function* sendMessageWorker() {
  const { currentUserId } = (yield select(authSelector)) as AuthState;
  const { stream, message } = (yield select(liveSelector)) as LiveState;
  if (!message || !stream?.id) return;
  const messageId = -Date.now();
  yield put(liveActions.setMessage(''));
  yield put(
    liveActions.addMessage({
      id: messageId,
      fromId: currentUserId,
      streamId: stream.id,
      createdAt: Date.now() / 1000,
      text: message,
      type: StreamMessageModelType.StreamMessageText,
      attachments: [],
    }),
  );
  try {
    const data = yield call(Api.post, `/streaming/im/${stream.id}/send`, {
      text: message,
    });
    yield put(
      liveActions.updateMessage({
        id: messageId,
        message: data.message,
      }),
    );
  } catch (e) {
    yield put(liveActions.setMessage(message));
    yield put(liveActions.deleteMessage(messageId));
    yield put(toastActions.setToastFail(e.message));
  }
}

function* toggleMicWorker() {
  const { mediaStream, micEnabled } = (yield select(liveSelector)) as LiveState;
  if (!mediaStream) return;
  const tracks = mediaStream.getAudioTracks();
  if (tracks.length === 0) return;
  for (const track of tracks) {
    track.enabled = micEnabled;
  }
}

function* getOnlineUsersWorker(actions: GetOnlineUsersAction) {
  const { stream } = (yield select(liveSelector)) as LiveState;
  if (!stream) return;
  try {
    if (!actions.payload.skipLoading) {
      yield put(liveActions.setOnlineUsersLoadingStatus('loading'));
    }
    const data = yield call(Api.get, `/streaming/info/${stream.id}/viewers`);
    yield put(mainActions.setUsersArray(data.viewers));
    yield put(liveActions.setOnlineUsers(data.viewers.map((user) => user.id)));
    yield put(liveActions.setOnlineUsersLoadingStatus('loaded'));
  } catch (e) {
    yield put(liveActions.setOnlineUsersLoadingStatus('failed'));
    console.error(e.message);
  }
}

function* getTotalTipsWorker(actions: GetTotalTipsAction) {
  const { stream } = (yield select(liveSelector)) as LiveState;
  if (!stream) return;
  try {
    if (!actions.payload.skipLoading) {
      yield put(liveActions.setTotalTipsLoadingStatus('loading'));
    }
    const data = yield call(Api.get, `/streaming/info/${stream.id}/tips`);
    yield put(liveActions.setTotalTips(data.total));
    yield put(liveActions.setTips(data.tips));
    yield put(mainActions.setUsers(data.users));
    yield put(liveActions.setTotalTipsLoadingStatus('loaded'));
  } catch (e) {
    yield put(liveActions.setTotalTipsLoadingStatus('failed'));
    console.error(e.message);
  }
}

export function* liveSaga() {
  yield all([
    watchIceConnectionStateChannel(),
    takeEvery(liveActions.createLive, createLiveWorker),
    takeEvery(liveActions.stopLive, stopLiveWorker),
    takeEvery(liveActions.closeLiveEndStatistics, closeLiveEndStatisticsWorker),
    takeEvery(liveActions.banned, stopStreamWorker),
    takeEvery(liveActions.startWatchLive, startWatchLiveWorker),
    takeEvery(liveActions.stopWatchLive, stopWatchLiveWorker),
    takeEvery(liveActions.startStream, startStreamWorker),
    takeEvery(liveActions.stopStream, stopStreamWorker),
    takeEvery(liveActions.initLive, initLiveWorker),
    takeEvery(liveActions.sendMessage, sendMessageWorker),
    takeEvery(liveActions.toggleMic, toggleMicWorker),
    takeLatest(liveActions.toggleCamera, setCameraIdWorker),
    takeEvery(liveActions.reconnect, reconnectionWorker),
    takeEvery(liveActions.getOnlineUsers, getOnlineUsersWorker),
    takeEvery(liveActions.getTotalTips, getTotalTipsWorker),
  ]);
}
